import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { fetchWithAuth } from "../../api/fetchWithAuth";
import { StatusBadge } from "./Badges";

export default function LicensingAnalyticsTable() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetchWithAuth("/api/v1/platform/licensing/analytics");
        if (!res.ok) throw new Error("Failed to load analytics licensing");
        setData(await res.json());
      } catch (e) {
        console.error(e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center pt-6">
        <Loader2 className="animate-spin text-gray-400" size={20} />
      </div>
    );
  }
  if (error) return <div className="text-xs text-red-400">{error}</div>;
  if (!data) return null;

  const { rows = [], totals = {} } = data;

  if (rows.length === 0) {
    return <div className="text-sm text-muted">No analytics grants found.</div>;
  }

  return (
    <div className="overflow-auto h-full">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-bg border-b border-bg-border">
          <tr className="text-left text-xs font-mono uppercase tracking-wide text-muted">
            <th className="px-3 py-2">Org</th>
            <th className="px-3 py-2">Product</th>
            <th className="px-3 py-2">AOI</th>
            <th className="px-3 py-2 text-right">Purchased</th>
            <th className="px-3 py-2 text-right">Consumed</th>
            <th className="px-3 py-2" text-center>Term</th>
            <th className="px-3 py-2">Status</th>
          </tr>
        </thead>

        <tbody>
          {rows.map(r => {
            const termStart = new Date(r.term_start).toLocaleDateString();
            const termEnd = r.term_end
              ? new Date(r.term_end).toLocaleDateString()
              : "—";
            const expiringSoon = r.term_end
              ? (new Date(r.term_end) - new Date()) / (1000 * 60 * 60 * 24) < 30
              : false;
            const isFlat = r.unit === "flat";

            return (
              <tr
                key={r.grant_id}
                className="border-b border-bg-border hover:bg-bg-soft"
              >
                <td className="px-3 py-2 font-medium">{r.org_name}</td>
                <td className="px-3 py-2">
                  {r.product_display_name}
                  {r.is_bundle && (
                    <span className="ml-2 text-accent-info text-[10px] uppercase">bundle</span>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-400">{r.aoi_name || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {isFlat
                    ? <span className="text-accent-info text-[10px] uppercase">flat-rate</span>
                    : r.sq_mi_purchased != null
                      ? Number(r.sq_mi_purchased).toFixed(1)
                      : "unmetered"}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-300">
                  {Number(r.sq_mi_consumed).toFixed(1)}
                </td>
                <td className={`px-3 py-2 ${expiringSoon ? "text-yellow-400" : "text-gray-400"}`}>
                  {termStart} – {termEnd}
                </td>
                <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
              </tr>
            );
          })}
        </tbody>

        <tfoot className="border-t border-bg-border bg-bg-soft sticky bottom-0">
          <tr>
            <td colSpan={3} className="px-3 py-2 text-right font-medium">
              Totals ({totals.grant_count} grants)
            </td>
            <td className="px-3 py-2 text-right font-semibold tabular-nums">
              {Number(totals.total_purchased).toFixed(1)}
            </td>
            <td className="px-3 py-2 text-right font-semibold tabular-nums">
              {Number(totals.total_consumed).toFixed(1)}
            </td>
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}